import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  IAginAuthorizationRequest,
  IAginAuthorizationResponse,
  IAginConnection,
  IAginConnectionCreateResponse,
  IAginConnectionPage,
  IAginPlatformWithConnections,
  IAginScope,
  IAginSubscriptionCreate,
  IAginSubscriptionCreateResponse,
} from '@cde-fe-app/app/shared/models/agin.model';
import { environment } from '@cde-fe-app/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AginConnectionService {
  private readonly baseUrl = `${environment.apiBaseUrl}/v1/agin`;
  private http = inject(HttpClient);

  getAginScopes(): Observable<IAginScope[]> {
    return this.http.get<IAginScope[]>(`${this.baseUrl}/scopes`);
  }

  getAginPlatformsWithConnections(): Observable<IAginPlatformWithConnections[]> {
    return this.http.get<IAginPlatformWithConnections[]>(`${this.baseUrl}/platforms-with-connections`);
  }

  createAginConnection(
    platformRequesting: { scopesGranted: string[] },
    platformResponding: { platformId: string }
  ): Observable<IAginConnectionCreateResponse> {
    return this.http.post<IAginConnectionCreateResponse>(`${this.baseUrl}/connections`, {
      platformRequesting,
      platformResponding,
    });
  }

  getAginConnections(page: number, size: number): Observable<IAginConnectionPage> {
    return this.http.get<IAginConnectionPage>(`${this.baseUrl}/connections`, {
      params: {
        page: page.toString(),
        size: size.toString(),
      },
    });
  }

  getAginConnection(connectionId: string): Observable<IAginConnection> {
    return this.http.get<IAginConnection>(`${this.baseUrl}/connections/${connectionId}`);
  }

  getAginConnectionWithAssets(connectionId: string): Observable<IAginConnection> {
    return this.http.get<IAginConnection>(`${this.baseUrl}/connections/${connectionId}`, {
      params: {
        include: 'remote-assets-with-subscriptions',
      },
    });
  }

  deleteAginConnection(connectionId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/connections/${connectionId}`);
  }

  getAginAuthorization(request: IAginAuthorizationRequest): Observable<IAginAuthorizationResponse> {
    return this.http.get<IAginAuthorizationResponse>(`${this.baseUrl}/connections/authorization`, {
      params: {
        scope: request.scope,
        connectionRequestId: request.connectionRequestId,
        platformRespondingId: request.platformRespondingId,
      },
    });
  }

  exchangeAginCodeForToken(platformId: string, code: string, state: string): Observable<void> {
    return this.http.get<void>(`${this.baseUrl}/connections/token`, {
      params: {
        platformRespondingId: platformId,
        code,
        state,
      },
    });
  }

  createSubscription(
    connectionId: string,
    subscriptionRequest: IAginSubscriptionCreate
  ): Observable<IAginSubscriptionCreateResponse> {
    return this.http.post<IAginSubscriptionCreateResponse>(
      `${this.baseUrl}/connections/${connectionId}/subscriptions`,
      subscriptionRequest
    );
  }

  deleteSubscription(connectionId: string, subscriptionId: string, assetId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/connections/${connectionId}/subscriptions/${subscriptionId}?assetId=${assetId}`
    );
  }
}
